import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import QuoteModal from "@/components/QuoteModal";
import useQuoteModal from "@/hooks/useQuoteModal";
import { motion, AnimatePresence } from "framer-motion";
import ProductNavigation from "@/components/ui/ProductNavigation";
import LazyImage from "@/components/LazyImage";
import { useTranslation } from 'react-i18next';
import {
  Star,
  Check,
  Shield,
  X,
  Phone,
  Mail,
  ShoppingCart,
  Sun,
  Award,
  Zap
} from "lucide-react";

const AutomobileDetail = () => {
  const { t } = useTranslation();

  const product = React.useMemo(() => ({
    id: "automotriz-sintetico",
    name: t('automobileDetail.productData.name'),
    description: t('automobileDetail.productData.description'),
    longDescription: t('automobileDetail.productData.longDescription'),
    features: [
      t('automobileDetail.productData.feature1'),
      t('automobileDetail.productData.feature2'),
      t('automobileDetail.productData.feature3'),
      t('automobileDetail.productData.feature4'),
      t('automobileDetail.productData.feature5'),
      t('automobileDetail.productData.feature6')
    ],
    specifications: [
      { name: t('automobileDetail.productData.spec1.name'), value: t('automobileDetail.productData.spec1.value') },
      { name: t('automobileDetail.productData.spec2.name'), value: t('automobileDetail.productData.spec2.value') },
      { name: t('automobileDetail.productData.spec3.name'), value: t('automobileDetail.productData.spec3.value') },
      { name: t('automobileDetail.productData.spec4.name'), value: t('automobileDetail.productData.spec4.value') },
      { name: t('automobileDetail.productData.spec5.name'), value: t('automobileDetail.productData.spec5.value') }
    ],
    applications: [
      t('automobileDetail.productData.app1'),
      t('automobileDetail.productData.app2'),
      t('automobileDetail.productData.app3'),
      t('automobileDetail.productData.app4'),
      t('automobileDetail.productData.app5')
    ],
    colors: [
      { name: t('automobileDetail.productData.color1'), hex: "#121212" },
      { name: t('automobileDetail.productData.color2'), hex: "#8c8c8c" },
      { name: t('automobileDetail.productData.color3'), hex: "#d8c9a3" },
      { name: t('automobileDetail.productData.color4'), hex: "#8b4513" },
      { name: t('automobileDetail.productData.color5'), hex: "#b22222" },
      { name: t('automobileDetail.productData.color6'), hex: "#1a2b4a" }
    ],
    mainImage: "/images/secciones-images/Automotriz/principal.webp",
    gallery: [
      "/images/secciones-images/Automotriz/uno.webp",
      "/images/secciones-images/Automotriz/dos.webp",
      "/images/secciones-images/Automotriz/tres.webp",
      "/images/secciones-images/Automotriz/cuatro.webp"
    ],
    rating: 4.9,
    reviews: 87,
    benefits: [
      {
        title: t('automobileDetail.productData.benefit1.title'),
        description: t('automobileDetail.productData.benefit1.description'),
        icon: Shield
      },
      {
        title: t('automobileDetail.productData.benefit2.title'),
        description: t('automobileDetail.productData.benefit2.description'),
        icon: Sun
      },
      {
        title: t('automobileDetail.productData.benefit3.title'),
        description: t('automobileDetail.productData.benefit3.description'),
        icon: Award
      },
      {
        title: t('automobileDetail.productData.benefit4.title'),
        description: t('automobileDetail.productData.benefit4.description'),
        icon: Zap
      }
    ]
  }), [t]);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedColor, setSelectedColor] = useState(0);
  const [activeTab, setActiveTab] = useState("descripcion");

  const {
    selectedProduct,
    openQuoteModal,
    closeQuoteModal,
    phoneNumbers,
    getWhatsAppUrl
  } = useQuoteModal();

  return (
    <div className="min-h-screen bg-black text-white">
      <ProductNavigation />
      {/* Hero Section */}
      <section className="relative h-[70vh] overflow-hidden">
        <div className="absolute inset-0">
          <LazyImage
            src="/images/home/detallesautomotriz2.webp"
            alt={t('automobileDetail.hero.altText')}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        </div>


        {/* Hero Content */}
        <div className="container mx-auto px-4 h-full flex items-center relative z-10">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-4 leading-tight" dangerouslySetInnerHTML={{ __html: t('automobileDetail.hero.title') }}>
              </h1>
              <div className="w-20 h-1 bg-red-600 mb-6"></div>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                {t('automobileDetail.hero.description')}
              </p>
              <div className="flex items-center space-x-4">
                <motion.button
                  onClick={() => openQuoteModal(product)}
                  className="bg-red-600 hover:bg-red-700 text-white py-3 px-8 rounded-full font-medium flex items-center space-x-2 transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <ShoppingCart className="h-5 w-5" />
                  <span>{t('automobileDetail.requestInfoBtn')}</span>
                </motion.button>
              </div>
            </motion.div>
          </div>
        </div>

      </section>

      {/* Main Content */}
      <section id="detalles" className="py-20 bg-gradient-to-b from-black to-gray-900">

        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left Column - Gallery */}
            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="sticky top-8"
              >
                <div className="relative aspect-square overflow-hidden rounded-xl mb-4">
                  <LazyImage
                    key={selectedImage}
                    src={product.gallery[selectedImage] || product.mainImage}
                    alt={product.name}
                    className="w-full h-full object-contain"
                  />
                  <div className="absolute top-4 right-4 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                    {t('automobileDetail.premiumBadge')}
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-2">
                  {product.gallery.map((image, index) => (
                    <motion.div
                      key={index}
                      className={`aspect-square rounded-lg overflow-hidden cursor-pointer ${selectedImage === index ? 'ring-2 ring-red-600' : ''
                        }`}
                      onClick={() => setSelectedImage(index)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <LazyImage
                        src={image || product.mainImage}
                        alt={`${product.name} - ${t('automobileDetail.galleryView')} ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </motion.div>
                  ))}
                </div>

                <div className="mt-8 bg-gray-800 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="flex items-center">
                      <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                      <span className="ml-1 font-medium">{product.rating}</span>
                    </div>
                    <span className="mx-2 text-gray-500">•</span>
                    <span className="text-gray-400">{product.reviews} {t('automobileDetail.reviewsLabel', { count: product.reviews })}</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-4">{t('automobileDetail.availableColors')}</h3>
                  <div className="flex flex-wrap gap-3">
                    {product.colors.map((color, index) => (
                      <motion.div
                        key={index}
                        className={`w-10 h-10 rounded-full cursor-pointer ${selectedColor === index ? 'ring-2 ring-red-600 ring-offset-2 ring-offset-gray-800' : ''
                          }`}
                        style={{ backgroundColor: color.hex }}
                        onClick={() => setSelectedColor(index)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        title={color.name}
                      />
                    ))}
                  </div>
                  <p className="text-sm text-gray-400 mt-2">
                    {t('automobileDetail.selectedColor')}: <span className="text-white">{product.colors[selectedColor].name}</span>
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Right Column - Details */}
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-4xl font-bold mb-6">{product.name}</h2>

                {/* Tabs */}
                <div className="border-b border-gray-800 mb-8">
                  <div className="flex overflow-x-auto space-x-4 sm:space-x-8">
                    {[
                      { id: "descripcion", name: t('automobileDetail.tabs.description') },
                      { id: "especificaciones", name: t('automobileDetail.tabs.specifications') },
                      { id: "aplicaciones", name: t('automobileDetail.tabs.applications') },
                      { id: "beneficios", name: t('automobileDetail.tabs.benefits') },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        className={`pb-4 px-2 font-medium transition-colors ${activeTab === tab.id
                          ? "text-red-600 border-b-2 border-red-600"
                          : "text-gray-400 hover:text-white"
                          }`}
                        onClick={() => setActiveTab(tab.id)}
                      >
                        {tab.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tab Content */}
                <div className="min-h-[400px]">
                  {/* Descripción */}
                  <AnimatePresence mode="wait">
                    {activeTab === "descripcion" && (
                      <motion.div
                        key="descripcion"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.5 }}
                      >
                        <p className="text-gray-300 mb-6 leading-relaxed">
                          {product.longDescription}
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                          {product.features.map((feature, index) => (
                            <div key={index} className="flex items-start">
                              <div className="bg-red-600 rounded-full p-1 mr-3 mt-0.5 flex-shrink-0">
                                <Check className="h-4 w-4 text-white" />
                              </div>
                              <span className="text-gray-300">{feature}</span>
                            </div>
                          ))}
                        </div>

                        <div className="mt-12 relative overflow-hidden rounded-xl aspect-video">
                          <video
                            className="w-full h-full object-cover"
                            autoPlay
                            muted
                            playsInline
                            loop
                            onEnded={(e) => {
                              e.target.currentTime = 0;
                              e.target.play();
                            }}
                          >
                            <source src="/images/secciones-images/Automotriz/interiorauto.webm" type="video/webm" />
                          </video>
                        </div>
                      </motion.div>
                    )}

                    {/* Especificaciones */}
                    {activeTab === "especificaciones" && (
                      <motion.div
                        key="especificaciones"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.5 }}
                      >
                        <div className="bg-gray-800 rounded-xl p-8">
                          <h3 className="text-2xl font-bold mb-6">{t('automobileDetail.specs.title')}</h3>
                          <div className="space-y-6">
                            {product.specifications.map((spec, index) => (
                              <div key={index} className="flex flex-col md:flex-row md:items-center justify-between pb-4 border-b border-gray-700">
                                <div className="text-gray-400 mb-2 md:mb-0">{spec.name}</div>
                                <div className="text-xl font-semibold">{spec.value}</div>
                              </div>
                            ))}
                          </div>

                          <div className="mt-8 bg-gradient-to-r from-red-900/30 to-red-600/30 rounded-lg p-6">
                            <h4 className="flex items-center text-lg font-semibold mb-4">
                              <Shield className="h-5 w-5 mr-2 text-red-500" />
                              {t('automobileDetail.specs.certificationsTitle')}
                            </h4>
                            <ul className="space-y-2 text-gray-300">
                              <li className="flex items-start">
                                <div className="bg-red-600 rounded-full p-1 mr-3 mt-0.5 flex-shrink-0">
                                  <Check className="h-3 w-3 text-white" />
                                </div>
                                <span>{t('automobileDetail.specs.certFlammability')}</span>
                              </li>
                              <li className="flex items-start">
                                <div className="bg-red-600 rounded-full p-1 mr-3 mt-0.5 flex-shrink-0">
                                  <Check className="h-3 w-3 text-white" />
                                </div>
                                <span>{t('automobileDetail.specs.certISO')}</span>
                              </li>
                              <li className="flex items-start">
                                <div className="bg-red-600 rounded-full p-1 mr-3 mt-0.5 flex-shrink-0">
                                  <Check className="h-3 w-3 text-white" />
                                </div>
                                <span>{t('automobileDetail.specs.certDurabilityTests')}</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Aplicaciones */}
                    {activeTab === "aplicaciones" && (
                      <motion.div
                        key="aplicaciones"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.5 }}
                        className="grid grid-cols-1 md:grid-cols-2 gap-6"
                      >
                        {product.applications.map((application, index) => {
                          // Mapear cada aplicación con su imagen correspondiente basada en el índice (1-indexed)
                          const imageNames = [
                            'asientos.webp',
                            'paneles.webp',
                            'techos.webp',
                            'volantes.webp',
                            'accesorios.webp'
                          ];

                          const imageName = imageNames[index] || 'principal.webp';

                          return (
                            <motion.div
                              key={index}
                              className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl overflow-hidden"
                              whileHover={{ y: -5, boxShadow: "0 20px 25px -5px rgba(220, 38, 38, 0.2)" }}
                            >
                              <LazyImage
                                src={`/images/secciones-images/Automotriz/${imageName}`}
                                alt={application}
                                className="h-60 w-full object-cover object-center"
                              />
                              <div className="p-6">
                                <h3 className="text-xl font-bold mb-2">{application}</h3>
                                <p className="text-gray-400">
                                  {t('automobileDetail.applications.description', { application: application.toLowerCase() })}
                                </p>
                              </div>
                            </motion.div>
                          );
                        })}
                      </motion.div>
                    )}

                    {/* Beneficios */}
                    {activeTab === "beneficios" && (
                      <motion.div
                        key="beneficios"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.5 }}
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                          {product.benefits.map((benefit, index) => (
                            <motion.div
                              key={index}
                              className="bg-gray-800 rounded-xl p-6"
                              initial={{ opacity: 0, y: 20 }}
                              animate={{
                                opacity: 1,
                                y: 0,
                                transition: { delay: index * 0.1 }
                              }}
                              whileHover={{
                                y: -5,
                                boxShadow: "0 20px 25px -5px rgba(220, 38, 38, 0.2)",
                                transition: { type: "spring", stiffness: 400, damping: 10 }
                              }}
                            >
                              <div className="w-12 h-12 bg-red-600/20 rounded-full flex items-center justify-center mb-4">
                                <benefit.icon className="h-6 w-6 text-red-500" />
                              </div>
                              <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                              <p className="text-gray-400">{benefit.description}</p>
                            </motion.div>
                          ))}
                        </div>

                        <div className="mt-12 bg-gradient-to-r from-red-900 to-red-700 rounded-xl overflow-hidden">
                          <div className="flex flex-col md:flex-row">
                            <div className="p-8 md:w-2/3">
                              <h3 className="text-2xl font-bold mb-4">{t('automobileDetail.benefits.ctaTitle')}</h3>
                              <p className="text-gray-200 mb-6">
                                {t('automobileDetail.benefits.ctaDescription')}
                              </p>
                              <motion.button
                                onClick={() => openQuoteModal(product)}
                                className="bg-white text-red-600 hover:bg-gray-100 py-3 px-8 rounded-full font-medium flex items-center space-x-2"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                              >
                                <ShoppingCart className="h-5 w-5" />
                                <span>{t('automobileDetail.requestInfoBtn')}</span>
                              </motion.button>
                            </div>
                            <div className="md:w-1/3 flex justify-center items-center p-4">
                              <motion.div
                                animate={{
                                  rotate: [0, 5, -5, 5, 0],
                                  y: [0, -5, 5, -5, 0]
                                }}
                                transition={{
                                  repeat: Infinity,
                                  duration: 5,
                                  ease: "easeInOut"
                                }}
                                className="w-full h-full"
                              >
                                <LazyImage
                                  src="/images/home/sautomotriz.webp"
                                  alt={t('automobileDetail.productData.altText')}
                                  className="w-full h-full max-w-[200px] max-h-[200px] object-contain"
                                />
                              </motion.div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

      </section>

      {/* Componente reutilizable de modal */}
      <QuoteModal
        isOpen={!!selectedProduct}
        onClose={closeQuoteModal}
        product={selectedProduct}
        selectedColor={selectedColor}
        phoneNumbers={phoneNumbers}
        getWhatsAppUrl={getWhatsAppUrl}
      />


    </div>
  );
};

export default AutomobileDetail;